import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { UserState } from '../models/user.state';

export interface AdminRoutePermission {
    path: string;
    featureSwitch: FeatureSwitchName[];
    permissionKey: string[];
}

@Injectable({
    providedIn: 'root',
})
export class PermissionsService {
    constructor(private featureService: FeatureService, private store: Store) {}

    // Full list of admin routes and their access configuration
    private adminRoutes: AdminRoutePermission[] = [
        {
            path: '/admin/course-offerings',
            featureSwitch: [FeatureSwitchName.CourseStructure],
            permissionKey: [AdminPermissions.COURSES_READ, AdminPermissions.COURSES_WRITE],
        },
        {
            path: '/admin/all-modules',
            featureSwitch: [FeatureSwitchName.AllModulesTab],
            permissionKey: [AdminPermissions.MODULES_READ, AdminPermissions.MODULES_WRITE],
        },
        {
            path: '/admin/users',
            featureSwitch: null,
            permissionKey: [AdminPermissions.USERS_READ, AdminPermissions.USERS_WRITE, AdminPermissions.PASSWORDLESS_ACCESS_WRITE],
        },
        {
            path: '/admin/grow',
            featureSwitch: [FeatureSwitchName.Engage],
            permissionKey: [AdminPermissions.GROW_WRITE],
        },
        {
            path: '/admin/dashboard',
            featureSwitch: [FeatureSwitchName.TenantDashboard],
            permissionKey: [AdminPermissions.DASHBOARD_READ],
        },
        {
            path: '/admin/question-library',
            featureSwitch: [FeatureSwitchName.ClasstimeSections],
            permissionKey: [AdminPermissions.QUESTION_LIBRARY_WRITE],
        },
        {
            path: '/admin/reports',
            featureSwitch: [FeatureSwitchName.Reporting, FeatureSwitchName.CourseSelfCancellation],
            permissionKey: [AdminPermissions.REPORTS_WRITE],
        },
        {
            path: '/admin/people-development',
            featureSwitch: [
                FeatureSwitchName.Teams,
                FeatureSwitchName.TrainingApprovalRequest,
                FeatureSwitchName.PerformanceFeedbackForm,
                FeatureSwitchName.CertificateManagement,
                FeatureSwitchName.CareerModel,
                FeatureSwitchName.Qualification,
            ],
            permissionKey: [
                AdminPermissions.TEAMS_WRITE,
                AdminPermissions.CERTIFICATION_WRITE,
                AdminPermissions.PERFORMANCE_REVIEW_WRITE,
                AdminPermissions.CAREER_MODEL_WRITE,
                AdminPermissions.QUALIFICATIONS_WRITE,
            ],
        },
        {
            path: '/admin/settings',
            featureSwitch: null,
            permissionKey: [AdminPermissions.SETTINGS_WRITE],
        },
    ];

    /**
     * Checks if the user has any of the specified permission keys
     */
    private hasPermission(keys: string[]): boolean {
        const userPermissions = this.store.selectSnapshot(UserState.getMyInfo).permissions || [];

        // If user has AdminPermissions.TENANT_MANAGEMENT, grant access regardless of keys
        if (userPermissions.includes(AdminPermissions.TENANT_MANAGEMENT)) {
            return true;
        }

        // Return true if keys is empty or user has at least one of the permissions in keys
        return keys.length === 0 || keys.some((key) => userPermissions.includes(key));
    }

    /**
     * Checks if a specific route is allowed (based on feature and permissions)
     */
    hasAccess(path: string): boolean {
        const route = this.adminRoutes.find((r) => r.path === path);
        if (!route) return false;

        const isFeatureActive = route.featureSwitch?.length ? route.featureSwitch.some((feature) => this.featureService.checkFeatureStatus(feature)) : true;
        const hasPermission = this.hasPermission(route.permissionKey);

        return isFeatureActive && hasPermission;
    }

    /**
     * Get the first available and permitted route
     */
    getFirstAvailableRoute(): string | null {
        for (const route of this.adminRoutes) {
            if (this.hasAccess(route.path)) {
                return route.path;
            }
        }
        return '/grow';
    }
}
